// String + label formatters shared by the card views (positions / orders /
// trades).

import { describeMarket, truncateTicker } from '../../lib/kalshiTicker/describeMarket';
import { roundToCentiCents } from '../../lib/pricing/subCent';

/** Price label — grain-canonical, trimmed ("56.9¢", "58.25¢", "55¢").
 *  The old Math.round displayed deci_cent order/position prices as
 *  integers on every card surface — prod-caught 2026-07-17. Volumes stay
 *  integer-rounded (fmtVol); quantities round only when integral (fmtQty);
 *  prices carry the 0.01¢ grain. */
export function fmtCents(c: number | null | undefined): string {
  return c != null && Number.isFinite(c) ? `${roundToCentiCents(c)}¢` : '—';
}

/** Quantity label (#144). Contract/share counts render as WHOLE numbers on every
 *  user-facing card + dashboard surface — Kalshi (integer-native) and Polymarket
 *  (fractional shares) alike. Display-only: the exact underlying quantity is never
 *  mutated, and accounting tables / CSV exports keep full precision (they do not
 *  use this formatter). Tradeoff accepted by #144: a Polymarket card's count now
 *  reads whole next to its exact `$1 × count` payout (supersedes the 2026-07-22
 *  2-decimal display). */
export function fmtQty(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
}

/**
 * Contract/share count, keeping fractions where `fmtQty` (#144) deliberately
 * rounds to whole numbers. Written for the merged position card (#982), shared
 * with Game Detail's Polymarket open orders (#1397).
 *
 * Whole values read whole ("1,725", matching every other card). Fractions show
 * up to 2 decimals, TRUNCATED toward zero, trailing zeros trimmed ("12.5",
 * "0.4", 0.999 → "0.99"): rounding would let 0.999 read "1" and a fractional
 * holding pass for a whole one. A nonzero value under 0.01 reads "<0.01",
 * never "0": a leftover that reads 0 says "flat" or "nothing resting" when it
 * isn't. Both rules apply after snapping to the nearest millionth, which
 * absorbs float noise: within half a millionth of a hundredth counts as that
 * hundredth (0.0099999… → "0.01", 0.2999995 → "0.3").
 */
export function fmtFractionalQty(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0'; // also -0, which toLocaleString renders "-0"
  // Snap to 1e-6 BEFORE the sliver check and the truncation, so float noise
  // can neither push an exact hundredth under it (10 − 9.99 is
  // 0.0099999…, #1397) nor truncate a real hundredth away (0.29 * 100 is
  // 28.999…). A nonzero value that snaps to 0 still reads "<0.01".
  const millionths = Math.round(n * 1e6);
  if (Math.abs(millionths) < 1e4) return n < 0 ? '-<0.01' : '<0.01';
  const hundredths = Math.trunc(millionths / 1e4);
  return (hundredths / 100).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

export function fmtUsd(d: number | null | undefined): string {
  if (d == null || !Number.isFinite(d)) return '—';
  return `$${d.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Signed dollars — "+$3.05" / "-$1.20". */
export function fmtUsdSigned(d: number | null | undefined): string {
  if (d == null || !Number.isFinite(d)) return '—';
  const sign = d >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(d).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Compact elapsed age — "8m", "6h", "3d". Empty string when unknown. */
export function fmtAge(
  sinceMs: number | null | undefined,
  nowMs: number = Date.now(),
): string {
  if (sinceMs == null || !Number.isFinite(sinceMs)) return '';
  const s = Math.max(0, Math.floor((nowMs - sinceMs) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/** Exact local timestamp for an aggregate's most recent fill. The optional
 * time zone exists for deterministic tests; production intentionally uses the
 * browser's local zone. Invalid/missing inputs remain unknown. */
export function fmtLastFillAt(
  value: string | null | undefined,
  timeZone?: string,
): string {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

/** Compact signed integer for velocity figures — "+1.4k", "-72k", "+340". */
export function fmtCompactSigned(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '+';
  const abs = Math.abs(n);
  let body: string;
  if (abs >= 1000) {
    const k = abs / 1000;
    body = `${k >= 100 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, '')}k`;
  } else {
    body = `${Math.round(abs)}`;
  }
  return `${sign}${body}`;
}

/** Split describeMarket's "MATCHUP — SPORT — DATE" string into a card title
 *  (matchup) plus a subtitle (sport · date · truncated ticker). Non-game
 *  tickers fall back to the truncated ticker as the title.
 *
 *  `team` and `titleTeam` are the market's named side for its TWO different
 *  jobs, and they are not always the same string:
 *
 *    team      → the standalone "(NYC) YES" side badge. Stays a short code.
 *    titleTeam → the token `BetTeamTitle` highlights INSIDE `title`, which for
 *                a feed-named matchup reads "New York City", not "NYC".
 *
 *  They were one field until #685 gave soccer cards the club names the
 *  dashboard row already showed; a whole-token search for the code then found
 *  nothing in the title and the highlight silently disappeared.
 *
 *    marketType → #964: an NFL ladder prop's stat ("Receiving Yards"). The
 *                badge then carries only the player and rung, and the card
 *                shows this as its own label, since a one-line badge may
 *                ellipsize (#603) and the stat is what tells two props apart. */
export function titleParts(
  ticker: string,
  side: 'yes' | 'no',
): { title: string; subtitle: string; team: string | null; titleTeam: string | null; marketType: string | null; badgeThreshold?: string } {
  const { marketLabel, betTeam, titleTeam, marketType, badgeTeam, badgeThreshold } = describeMarket(ticker, side);
  const [head, ...rest] = marketLabel.split(' — ');
  const subtitle = [...rest, truncateTicker(ticker)].join(' · ');
  return { title: head || marketLabel, subtitle, team: badgeTeam ?? betTeam, titleTeam, marketType: marketType ?? null,
    ...(badgeThreshold ? { badgeThreshold } : {}) };
}

/** #964 — the text a cancel confirmation, pin snapshot or edit-price dialog
 *  names an order or position by. The whole bet leads ("K. Johnson #26 15+
 *  Receiving Yards — MIN @ DET — NFL · 09-27"), so a one-line ellipsis can only
 *  cut the matchup, and every such surface means one thing. `label` keeps its
 *  own tail (a pin's ticker tail stays last). No bet (Polymarket, an
 *  unparseable ticker) leaves `label` unchanged. */
export function ticketLabel(label: string, bet: string | null | undefined): string {
  return bet ? `${bet} — ${label}` : label;
}

/** The whole bet a card VM stands for: its badge label plus the NFL ladder
 *  stat the card shows beside it (`describeMarket.betTeam`, rebuilt). */
export function vmBet(vm: { team: string | null; marketType?: string | null }): string | null {
  if (!vm.team) return null;
  return vm.marketType ? `${vm.team} ${vm.marketType}` : vm.team;
}

/** Contract volume — "1.2M / 12.3K / 999". Mirrors the ladder's
 *  formatVolume (Math.round before stringifying: Kalshi volumes are
 *  floats and sub-1k fractions must never render raw). Lives here so
 *  components/ don't import from views/. */
export function fmtVol(vol: number | null | undefined): string {
  if (vol == null || !Number.isFinite(vol) || vol === 0) return '—';
  if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 10_000) return `${(vol / 1_000).toFixed(1)}K`;
  const rounded = Math.round(vol);
  if (rounded >= 1_000) return rounded.toLocaleString();
  return String(rounded);
}
